import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  TextField,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Autocomplete,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import AssignmentIcon from "@mui/icons-material/Assignment";
import EditLocationAltOutlinedIcon from "@mui/icons-material/EditLocationAltOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import GavelIcon from "@mui/icons-material/Gavel";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import AppDialog from "../General/Dialog/AppDialog";
import TemplateEditor from "../General/TemplateEditor";
import { useToast } from "../../context/ToastContext";

function AddressCard({ address, onUse, selected = false }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.6,
        borderRadius: 3,
        height: "100%",
        background: selected
          ? "rgba(25,118,210,0.10)"
          : isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.62)",
        border: selected
          ? "1px solid rgba(25,118,210,0.24)"
          : isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
        boxShadow: "none",
        transition: "all .2s ease",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Chip
            size="small"
            icon={<HomeOutlinedIcon sx={{ fontSize: 16 }} />}
            label={address.type || "Sin tipo"}
            sx={{
              borderRadius: 2,
              fontWeight: 800,
              bgcolor: "rgba(255,255,255,0.75)",
            }}
          />
          {selected && (
            <Chip
              size="small"
              icon={<TaskAltOutlinedIcon sx={{ fontSize: 16 }} />}
              label="Seleccionado"
              sx={{
                borderRadius: 2,
                fontWeight: 900,
                bgcolor: "rgba(46,125,50,0.12)",
                color: "success.main",
              }}
            />
          )}
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 850, lineHeight: 1.25 }}>
            {address.address || "-"}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            {address.city || "Localidad no informada"}
          </Typography>
        </Box>

        <Typography variant="caption" sx={{ opacity: 0.72 }}>
          Desde: {address.from_date || "-"}
        </Typography>
      </Box>

      <Button
        size="small"
        variant={selected ? "contained" : "outlined"}
        onClick={onUse}
        sx={{ mt: 1.5, borderRadius: 2.2, fontWeight: 900, textTransform: "none" }}
      >
        {selected ? "Domicilio en uso" : "Usar este domicilio"}
      </Button>
    </Paper>
  );
}

function SectionBlock({ icon, title, description, children }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 3,
        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.55)",
        border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
        boxShadow: "none",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.2,
          mb: children ? 1.5 : 0,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2.5,
            display: "grid",
            placeItems: "center",
            bgcolor: "rgba(25,118,210,0.10)",
            color: "primary.main",
            border: "1px solid rgba(25,118,210,0.16)",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, fontSize: 15.5, lineHeight: 1.15 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="body2" sx={{ opacity: 0.72, mt: 0.25 }}>
              {description}
            </Typography>
          ) : null}
        </Box>
      </Box>

      {children}
    </Paper>
  );
}

function buildDefaultTemplateHtml() {
  return `
    <blockquote><strong>WHATSAPP:</strong> {{lawyer_phone}} <br><strong>Ref:</strong> {{lawsuit_code}}</blockquote>
    <h1 style="text-align: center;">MUNICIPALIDAD</h1>
    <h1 style="text-align: center;">{{lawyer_document}}</h1>
    <h2 style="text-align: center">CÉDULA DE NOTIFICACIÓN</h2>

    <p><strong>TRIBUNAL:</strong> {{dependencies}} <br></p>
    <p><strong>SECRETARÍA:</strong> ÚNICA</p>
    <p><strong>DOMICILIO TRIBUNAL:</strong> ARTURO M. BAS 244</p>
    <p><strong>SEÑOR/A:</strong> {{party_name}}</p>
    <p><strong>DOMICILIO:</strong> {{party_address}}</p>

    <p style="text-align: justify"><strong>Se hace saber a Ud. que se ha dictado la siguiente resolución en el </strong> {{operation_text}}. Se acompaña copia de documental en ... fjs</p>

    <h4 style="text-align: center"><strong>{{lawyer_full_name}}</strong><br><strong>ABOGADO</strong><br><strong>MP: {{lawyer_document}}</strong></h4>

  `;
}

function normalizeJusticeAddresses(addresses = []) {
  return (addresses || []).map((addr, index) => ({
    id: `justice-${index}`,
    type: addr?.tipoDomicilio || "",
    address: addr?.direccion || "",
    city: addr?.localidad || "",
    from_date: addr?.fechaDesde
      ? new Date(addr.fechaDesde).toISOString().split("T")[0]
      : null,
    is_manual: false,
  }));
}

export default function NotificationCertificateFromOperationModal({
  open,
  onClose,
  onConfirm,
  loading = false,
  legalFile = null,
  selectedOperation = null,
  operationOptions = [],
  apiError = "",
  onClearApiError,
}) {
  const { showToast } = useToast();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fileInputRef = useRef(null);

  const [activeStep, setActiveStep] = useState(0);

  const [form, setForm] = useState({
    operation: null,
    secondaryOperation: null,
    selectedAddress: {
      type: "",
      address: "",
      city: "",
      from_date: "",
      is_manual: true,
    },
    attachmentFile: null,
    templateHtml: "",
  });

  const mainDefendant = useMemo(() => {
    return (legalFile?.parties || []).find(
      (party) =>
        String(party.role || "").trim().toUpperCase() === "DEMANDADO PRINCIPAL",
    );
  }, [legalFile]);

  const justiceAddresses = useMemo(() => {
    return normalizeJusticeAddresses(mainDefendant?.addresses || []);
  }, [mainDefendant]);

  const lawsuitAddress = useMemo(() => {
    const lawsuit = legalFile?.lawsuits;
    if (!lawsuit?.address) return null;
    return {
      type: "Título",
      address: lawsuit.address || "",
      city: "",
      from_date: "",
      is_manual: false,
    };
  }, [legalFile]);

  useEffect(() => {
    if (!open) return;

    const initialAddress = lawsuitAddress || justiceAddresses[0] || {
      type: "",
      address: "",
      city: "",
      from_date: "",
      is_manual: true,
    };

    setForm({
      operation: selectedOperation || null,
      secondaryOperation: null,
      selectedAddress: initialAddress,
      attachmentFile: null,
      templateHtml: buildDefaultTemplateHtml(),
    });

    setActiveStep(0);  }, [open, selectedOperation, justiceAddresses, lawsuitAddress]);

  const steps = [
    { key: "operations", label: "Operaciones" },
    { key: "addresses", label: "Domicilio" },
    { key: "attachment", label: "Adjunto" },
    { key: "template", label: "Plantilla" },
  ];

  const handleClose = () => {
    if (loading) return;    onClearApiError?.();
    onClose?.();
  };

  const handleNext = () => {
    if (activeStep === 0 && !form.operation) {
      showToast("Debe seleccionar una operación principal.", "error")
      return;
    }

    if (activeStep === 1 && !form.selectedAddress?.address?.trim()) {
      showToast(
        "Debe tener una dirección seleccionada o completada manualmente.",
      "error")
      return;
    }

    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleAddressChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      selectedAddress: {
        ...prev.selectedAddress,
        [field]: value,
        is_manual: true,
      },
    }));
  };

  const handleUseAddress = (address) => {
    setForm((prev) => ({
      ...prev,
      selectedAddress: {
        type: address.type || "",
        address: address.address || "",
        city: address.city || "",
        from_date: address.from_date || "",
        is_manual: false,
      },
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setForm((prev) => ({ ...prev, attachmentFile: file }));
    e.target.value = "";
  };

  const handleRemoveFile = () => {
    setForm((prev) => ({ ...prev, attachmentFile: null }));
  };

  const handleRestoreTemplate = () => {
    setForm((prev) => ({ ...prev, templateHtml: buildDefaultTemplateHtml() }));
  };

  const handleConfirm = () => {
    if (!form.operation) {
      showToast("Debe seleccionar una operación principal.", "error")
      setActiveStep(0);
      return;
    }

    if (!mainDefendant) {
      showToast("No se encontró el demandado principal.", "error")
      return;
    }

    if (!form.selectedAddress?.address?.trim()) {
      showToast("Debe seleccionar o escribir un domicilio.", "error")
      setActiveStep(1);
      return;
    }

    onConfirm?.({
      operation: form.operation,
      secondaryOperation: form.secondaryOperation,
      party: mainDefendant,
      selectedAddress: form.selectedAddress,
      attachmentFile: form.attachmentFile,
      templateHtml: form.templateHtml || "",
    });
  };

  const isAddressSelected = (address) =>
    form.selectedAddress.type === (address.type || "") &&
    form.selectedAddress.address === (address.address || "") &&
    form.selectedAddress.city === (address.city || "") &&
    form.selectedAddress.from_date === (address.from_date || "") &&
    !form.selectedAddress.is_manual;

  const renderStepContent = () => {
    if (activeStep === 0) {
      return (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <SectionBlock
              icon={<AssignmentIcon />}
              title={legalFile?.code || "Expediente"}
              description="La operación seleccionada será la principal. Podés agregar una secundaria."
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              options={operationOptions}
              value={form.operation}
              onChange={(_, value) =>
                setForm((prev) => ({
                  ...prev,
                  operation: value,
                  secondaryOperation:
                    prev.secondaryOperation?.id === value?.id
                      ? null
                      : prev.secondaryOperation,
                }))
              }
              getOptionLabel={(option) =>
                `${option.number_operation} - ${option.type || "Sin tipo"}`
              }
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Operación principal"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.72)",
                      borderRadius: 2,
                    },
                  }}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              options={operationOptions.filter(
                (op) => op.id !== form.operation?.id,
              )}
              value={form.secondaryOperation}
              onChange={(_, value) =>
                setForm((prev) => ({ ...prev, secondaryOperation: value }))
              }
              getOptionLabel={(option) =>
                `${option.number_operation} - ${option.type || "Sin tipo"}`
              }
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Operación secundaria (opcional)"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.72)",
                      borderRadius: 2,
                    },
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      );
    }

    if (activeStep === 1) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SectionBlock
            icon={<EditLocationAltOutlinedIcon />}
            title="Domicilio que se usará en la cédula"
            description="Podés editarlo manualmente o usar uno de los domicilios disponibles abajo."
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Tipo"
                  value={form.selectedAddress.type || ""}
                  onChange={(e) => handleAddressChange("type", e.target.value)}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 8 }}>
                <TextField
                  label="Dirección"
                  value={form.selectedAddress.address || ""}
                  onChange={(e) =>
                    handleAddressChange("address", e.target.value)
                  }
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Localidad"
                  value={form.selectedAddress.city || ""}
                  onChange={(e) => handleAddressChange("city", e.target.value)}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Fecha Desde"
                  type="date"
                  value={form.selectedAddress.from_date || ""}
                  onChange={(e) =>
                    handleAddressChange("from_date", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                      borderRadius: 2,
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Alert
                  severity="info"
                  icon={<InfoOutlinedIcon />}
                  sx={{ borderRadius: 2.5, mt: 0.5 }}
                >
                  Si modificás este bloque manualmente, ese domicilio será el
                  que se utilice al generar la cédula.
                </Alert>
              </Grid>
            </Grid>
          </SectionBlock>

          <Divider sx={{ my: 0.5 }} />

          <SectionBlock
            icon={<GavelOutlinedIcon />}
            title="Domicilios de la parte"
            description="Domicilios provenientes de justicia del demandado principal."
          >
            {justiceAddresses.length > 0 ? (
              <Grid container spacing={1.5}>
                {justiceAddresses.map((address, i) => (
                  <Grid key={i} size={{ xs: 12, md: 6 }}>
                    <AddressCard
                      address={address}
                      selected={isAddressSelected(address)}
                      onUse={() => handleUseAddress(address)}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2.5 }}>
                No hay domicilios de justicia para el demandado principal.
              </Alert>
            )}
          </SectionBlock>

          {lawsuitAddress && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <SectionBlock
                icon={<GavelIcon />}
                title="Domicilio del título"
                description="Dirección registrada en el título asociado al expediente."
              >
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <AddressCard
                      address={lawsuitAddress}
                      selected={isAddressSelected(lawsuitAddress)}
                      onUse={() => handleUseAddress(lawsuitAddress)}
                    />
                  </Grid>
                </Grid>
              </SectionBlock>
            </>
          )}
        </Box>
      );
    }

    if (activeStep === 2) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <SectionBlock
            icon={<UploadFileRoundedIcon />}
            title="Adjuntar Título"
            description={
              legalFile?.lawsuits
                ? "El expediente ya tiene un Título asociado. Si adjuntás un PDF, se usará en su lugar."
                : "El expediente no tiene Título asociado. Podés subir el PDF del título para incluirlo en la cédula."
            }
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />

            {!form.attachmentFile ? (
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  mt: 0.5,
                  p: 3,
                  borderRadius: 3,
                  border: "2px dashed rgba(25,118,210,0.25)",
                  background: "rgba(25,118,210,0.04)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  transition: "all .2s",
                  "&:hover": {
                    borderColor: "primary.main",
                    background: "rgba(25,118,210,0.08)",
                  },
                }}
              >
                <UploadFileRoundedIcon
                  sx={{ fontSize: 38, color: "primary.main", opacity: 0.8 }}
                />
                <Typography sx={{ fontWeight: 900 }}>
                  Hacé clic para seleccionar el archivo
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  Formato aceptado: PDF
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 0.5,
                  p: 2,
                  borderRadius: 3,
                  border: "1px solid rgba(46,125,50,0.24)",
                  background: "rgba(46,125,50,0.06)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <InsertDriveFileOutlinedIcon
                  sx={{ color: "success.main", fontSize: 32, flexShrink: 0 }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 900 }} noWrap>
                    {form.attachmentFile.name}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.72 }}>
                    {(form.attachmentFile.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleRemoveFile}
                  sx={{ flexShrink: 0 }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {legalFile?.lawsuits ? (
              <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2.5 }}>
                Este paso es <strong>opcional</strong>. El expediente ya tiene
                un Título, por lo que podés continuar sin adjuntar ningún
                archivo.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2.5 }}>
                El expediente <strong>no tiene Título asociado</strong>. Podés
                subir el PDF del título para incluirlo en la cédula, o
                continuar sin él (la cédula se generará igualmente).
              </Alert>
            )}
          </SectionBlock>
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionBlock
          icon={<DescriptionOutlinedIcon />}
          title="Plantilla editable"
          description="Ajustá el contenido antes de generar la cédula."
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.2 }}>
            <Button
              startIcon={<RefreshOutlinedIcon />}
              onClick={handleRestoreTemplate}
              variant="outlined"
              sx={{ borderRadius: 2.2, fontWeight: 800, textTransform: "none" }}
            >
              Restaurar plantilla
            </Button>
          </Box>

          <TemplateEditor
            value={form.templateHtml}
            onChange={(val) =>
              setForm((prev) => ({ ...prev, templateHtml: val }))
            }
          />

          <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2.5 }}>
            El contenido se enviará como HTML al backend para generar la cédula.
          </Alert>
        </SectionBlock>
      </Box>
    );
  };

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title="Generar cédula de notificación"
      subtitle="Seleccioná operación, domicilio, adjunto y plantilla."
      maxWidth="lg"
      fullScreenMobile
      fullScreenBreakpoint="md"
      stickyHeader
      contentSx={{
        p: { xs: 1.2, sm: 2 },
        background: "transparent",
      }}
      paperSx={{ borderRadius: isMobile ? 0 : 4, overflow: "hidden" }}
      actions={
        <>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              borderRadius: 2.5,
              fontWeight: 800,
              textTransform: "none",
              px: 2.2,
            }}
          >
            Cancelar
          </Button>

          {activeStep > 0 && (
            <Button
              onClick={handleBack}
              disabled={loading}
              sx={{ borderRadius: 2.5, fontWeight: 800, textTransform: "none" }}
            >
              Volver
            </Button>
          )}

          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading}
              sx={{
                borderRadius: 2.5,
                fontWeight: 900,
                textTransform: "none",
                px: 2.8,
              }}
            >
              Siguiente
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={loading}
              sx={{
                borderRadius: 2.5,
                fontWeight: 900,
                textTransform: "none",
                px: 2.8,
                minWidth: 180,
              }}
            >
              {loading ? "Generando..." : "Guardar y generar"}
            </Button>
          )}
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

        {apiError ? (
          <Alert
            severity="error"
            onClose={() => onClearApiError?.()}
            sx={{ borderRadius: 2.5 }}
          >
            {apiError}
          </Alert>
        ) : null}

        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 3,
            background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.55)",
            border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <Stepper
            activeStep={activeStep}
            alternativeLabel={!isMobile}
            sx={{
              "& .MuiStepConnector-line": {
                borderColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
                borderTopWidth: 2,
                borderRadius: 1,
              },
              "& .MuiStepIcon-root": {
                color: isDark ? "rgba(255,255,255,0.20)" : "rgba(0,0,0,0.15)",
                width: 28,
                height: 28,
              },
              "& .MuiStepIcon-root.Mui-active": { color: "primary.main" },
              "& .MuiStepIcon-root.Mui-completed": { color: "primary.main" },
              "& .MuiStepLabel-label": {
                fontWeight: 600,
                color: "text.secondary",
                mt: 0.5,
              },
              "& .MuiStepLabel-label.Mui-active": {
                color: "primary.main",
                fontWeight: 900,
              },
              "& .MuiStepLabel-label.Mui-completed": {
                color: "primary.main",
                fontWeight: 800,
              },
            }}
          >
            {steps.map((step) => (
              <Step key={step.key}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {renderStepContent()}
      </Box>
    </AppDialog>
  );
}
